import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { SORT } from '@/components/list-bar/ListBarProfile';
import useCreateQueryString from '@/hooks/useCreateQueryString';
import type { IGetAssetWithAddress } from '@/services';
import { getAssetWithAddress } from '@/services';
import type { IOffer } from '@/types/asset';

const PAGE_SIZE = 20;
export interface AssetData {
  id: number;
  picture: string;
  name: string;
  collection: {
    id: number;
    name: string;
  } | null;
  mimeType: string;
  model: {
    id: number;
    name: string;
  };
  description: string;
  offer: IOffer | null;
  prompt: number | null;
  accountAddress: string | null;
  url: string;
  collectionId: number | null;
  isNsfw?: boolean;
}

const useUserArtwork = (address: string, isAddCollection?: boolean) => {
  const assetDataRef = useRef([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [assetData, setAssetData] = useState<AssetData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [sort, setSort] = useState(SORT.desc);
  const [search, setSearch] = useState('');
  const searchParam = useSearchParams();
  const pageParam = searchParam.get('page');
  const pageWatch = pageParam ? parseInt(pageParam) : 1;
  const createQueryString = useCreateQueryString();

  const handleGetArtwork = async (page: number) => {
    setLoading(true);
    const queryParams: IGetAssetWithAddress = {
      pageSize: PAGE_SIZE,
      offset: page ? (Number(page) - 1) * PAGE_SIZE : 0,
      address,
    };
    if (sort) {
      queryParams.sort = sort;
    }
    if (search) {
      queryParams.search = search;
    }
    const res = await getAssetWithAddress(queryParams);

    if (page === 1) {
      assetDataRef.current = [];
      setAssetData([]);
    }
    setTotalItems(res?.data.total);
    assetDataRef.current = assetDataRef.current.concat(res?.data?.items || []);
    if (isAddCollection) setAssetData(assetDataRef.current);
    else setAssetData(res?.data?.items || []);
    setLoading(false);
  };

  const loadMore = (page: number) => {
    setCurrentPage(page);
    handleGetArtwork(page);
  };

  useEffect(() => {
    createQueryString(['page', 'tab'], [1, 'artwork']);
    handleGetArtwork(1);
  }, [search, sort]);

  useEffect(() => {
    setCurrentPage(pageWatch);
    handleGetArtwork(pageWatch);
  }, [pageWatch]);

  return {
    currentPage,
    setCurrentPage,
    artworks: assetData,
    loading,
    search,
    sort,
    setSearch,
    setSort,
    loadMore,
    totalItems,
  };
};

export default useUserArtwork;
